import React from "react";
import { AlertTriangle, RotateCw } from "lucide-react";

/**
 * Global error boundary so a single bad render never shows a blank page.
 * Logs to console only — preserves UX trust by offering a clear retry path
 * and a contact link instead of a cryptic stack trace.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[FlowFinder] uncaught render error:", error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        className="min-h-screen flex items-center justify-center px-6 bg-[#050507]"
        data-testid="error-boundary"
      >
        <div className="max-w-md w-full text-center">
          <div className="w-12 h-12 mx-auto rounded-2xl bg-white/[0.04] border border-[var(--bear)]/40 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-[var(--bear)]" />
          </div>
          <h1 className="text-white text-2xl font-semibold tracking-tight mt-5">
            Something went wrong
          </h1>
          <p className="text-white/65 text-sm mt-2">
            FlowFinder hit an unexpected error rendering this page. Try reloading; if it keeps happening, email
            {" "}
            <a className="text-[var(--bull)]" href="mailto:flowhelp@yahoo.com">flowhelp@yahoo.com</a>
            {" "}and include what you were doing.
          </p>
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              onClick={() => window.location.reload()}
              className="btn-accent text-sm py-2 px-4 inline-flex items-center gap-2"
              data-testid="error-boundary-reload"
            >
              <RotateCw className="w-4 h-4" />
              Reload
            </button>
            <button
              onClick={this.reset}
              className="btn-ghost text-sm py-2 px-4"
              data-testid="error-boundary-retry"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}
